/**
 * Mesure des blocs de recommandation : carte vue, clic vers la fiche, ajout au panier.
 *
 * Les événements partent par lots pour ne pas multiplier les appels. Le dernier lot part
 * aussi quand l'onglet se ferme ou passe en arrière-plan, d'où `keepalive`.
 *
 * Tout est best-effort : une mesure perdue ne doit jamais gêner la navigation ni le panier.
 */
import {
  recommendEventsSchema,
  type RecommendEvent,
  type RecommendEventType,
} from '@/lib/restSchemas/recommend'
import { getPharmeSessionId, SESSION_HEADER } from '@/lib/sessionTracking'
import { ApiMutationEndpoint } from '@/src/ApiMutationEndpoint'

const FLUSH_DELAY_MS = 3000
const BATCH_SIZE = 40

let queue: RecommendEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let listening = false

const flush = (): void => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (queue.length === 0) return

  const events = queue.splice(0, BATCH_SIZE)
  if (queue.length > 0) timer = setTimeout(flush, FLUSH_DELAY_MS)

  const body = recommendEventsSchema.safeParse({ events })
  if (!body.success) return

  const sessionId = getPharmeSessionId()
  const { endpoint, method } = ApiMutationEndpoint.CATALOG.RECOMMEND_EVENTS
  fetch(`/api/csCartProxy?url=${encodeURIComponent(endpoint)}`, {
    method,
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
    body: JSON.stringify(body.data),
  }).catch(() => {})
}

const listenForPageExit = (): void => {
  if (listening) return
  listening = true
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
}

export const trackRecommendEvent = (
  type: RecommendEventType,
  productId: string | number,
  placement: string,
  rank: number
): void => {
  const id = Number(productId)
  // Un produit récent peut n'avoir que son slug : l'écarter évite de perdre tout le lot au parse.
  if (typeof window === 'undefined' || !Number.isInteger(id) || id <= 0) return

  listenForPageExit()
  queue.push({ type, product_id: id, placement, rank })

  if (queue.length >= BATCH_SIZE) {
    flush()
  } else if (!timer) {
    timer = setTimeout(flush, FLUSH_DELAY_MS)
  }
}
